<template>
  <div class="rm-screen">
    <div class="rm-segment" role="tablist" :aria-label="$t('report_mobile.group_by')">
      <button
        type="button"
        role="tab"
        class="rm-segment__btn"
        :class="{ 'is-active': groupBy === 'meal' }"
        :aria-selected="String(groupBy === 'meal')"
        @click="groupBy = 'meal'"
      >
        {{ $t("report_mobile.by_meal") }}
      </button>
      <button
        type="button"
        role="tab"
        class="rm-segment__btn"
        :class="{ 'is-active': groupBy === 'worker' }"
        :aria-selected="String(groupBy === 'worker')"
        @click="groupBy = 'worker'"
      >
        {{ $t("report_mobile.by_worker") }}
      </button>
    </div>

    <label class="rm-search">
      <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
      <span class="rm-visually-hidden">{{ $t("report_mobile.search_workers") }}</span>
      <input v-model="searchModel" type="search" :placeholder="$t('report_mobile.search_workers')" />
    </label>

    <div v-if="!selectedCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!orderCompanies.length" class="rm-empty">
      {{
        filters.search
          ? $t("report_mobile.no_results_for", { search: filters.search })
          : $t("report_mobile.no_meals")
      }}
    </div>

    <template v-else>
      <section v-for="company in orderCompanies" :key="company.id" class="rm-orders-company">
        <div class="rm-orders-company__header">
          <span class="rm-section-title">{{ company.name }}</span>
          <span class="rm-muted rm-num">
            {{ pluralT("report_mobile.workers_n", company.workers.length) }}
          </span>
        </div>

        <template v-if="groupBy === 'meal'">
          <section v-for="meal in company.meals" :key="meal.key" class="rm-card rm-orders-meal">
            <button
              type="button"
              class="rm-orders-meal__head"
              :aria-expanded="String(isMealOpen(meal.key))"
              @click="toggleMeal(meal.key)"
            >
              <span class="rm-orders-meal__name">{{ meal.name }}</span>
              <span class="rm-count-pill">{{ meal.count }}</span>
              <v-icon
                size="18"
                color="#8a817a"
                class="rm-orders-meal__chevron"
                :class="{ 'is-open': isMealOpen(meal.key) }"
              >
                mdi-chevron-down
              </v-icon>
            </button>
            <ul v-if="isMealOpen(meal.key)" class="rm-list rm-orders-people">
              <li v-for="person in meal.people" :key="person.key" class="rm-orders-person">
                <span class="rm-avatar">{{ person.initials }}</span>
                <span class="rm-orders-person__name">{{ person.name }}</span>
                <span v-if="person.quantity > 1" class="rm-orders-person__qty rm-num">
                  {{ person.quantity }}×
                </span>
              </li>
            </ul>
          </section>
        </template>

        <ul v-else class="rm-list rm-card">
          <li v-for="worker in company.workers" :key="worker.id" class="rm-orders-worker">
            <span class="rm-avatar rm-avatar--lg">{{ worker.initials }}</span>
            <span class="rm-orders-worker__text">
              <span class="rm-orders-worker__name">{{ worker.name }}</span>
              <span v-for="item in worker.items" :key="item.key" class="rm-muted">
                {{ item.quantity }}× {{ item.meal }}
              </span>
            </span>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import ReportMealsTabMealEmployee from "@/components/report/meals/tabs/ReportMealsTabMealEmployee.vue";
import { getShiftKey } from "@/constants/shifts";
import hasPlural from "../hasPlural";

const LAYOUT_CARDS = 0;
const LAYOUT_TABLE = 1;

/**
 * Mobilni prikaz "Narudžbe radnika" (naručeno + obrok-radnici): nasleđuje učitavanje,
 * pretragu, štampu i Excel iz ReportMealsTabMealEmployee.
 */
export default {
  name: "ReportMobileWorkerOrders",
  extends: ReportMealsTabMealEmployee,
  mixins: [hasPlural],
  props: {
    shifts: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    groupBy: "meal",
    closedMeals: {},
  }),
  computed: {
    searchModel: {
      get() {
        return this.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
    orderCompanies() {
      return this.meals
        .map((entry) => {
          const meals = entry.meals
            .map((meal) => this.toOrderMeal(entry.id, meal))
            .filter((meal) => meal.people.length);
          return {
            id: entry.id,
            name: this.toLatin(entry.company?.name || ""),
            meals,
            workers: this.groupByWorker(meals),
          };
        })
        .filter((company) => company.meals.length);
    },
  },
  watch: {
    shifts: {
      handler(shifts) {
        this.selectedShifts = [...shifts];
      },
      immediate: true,
    },
    groupBy: {
      handler(groupBy) {
        // Štampa prati izabrani prikaz: po jelu → kartice, po radniku → tabela.
        this.selectedLayout = groupBy === "meal" ? LAYOUT_CARDS : LAYOUT_TABLE;
      },
      immediate: true,
    },
  },
  methods: {
    toOrderMeal(companyId, meal) {
      const people = [];
      this.selectedShifts.forEach((shiftId) => {
        (meal[getShiftKey(shiftId)] || []).forEach((user) => {
          people.push({
            key: `${shiftId}-${user.id}`,
            id: user.id,
            name: this.fullName(user),
            initials: this.initials(user),
            quantity: user.quantity || 1,
            shiftId,
          });
        });
      });
      people.sort((a, b) => a.name.localeCompare(b.name));
      return {
        key: `${companyId}-${meal.id}`,
        name: meal.name,
        count: meal.total,
        people,
      };
    },
    groupByWorker(meals) {
      const workers = {};
      meals.forEach((meal) => {
        meal.people.forEach((person) => {
          if (!workers[person.id]) {
            workers[person.id] = {
              id: person.id,
              name: person.name,
              initials: person.initials,
              items: [],
            };
          }
          workers[person.id].items.push({
            key: `${meal.key}-${person.shiftId}`,
            meal: meal.name,
            quantity: person.quantity,
            shiftId: person.shiftId,
          });
        });
      });
      return Object.values(workers).sort((a, b) => a.name.localeCompare(b.name));
    },
    fullName(user) {
      return [user.name, user.lastname].filter(Boolean).map(this.toLatin).join(" ");
    },
    initials(user) {
      return [user.name, user.lastname]
        .filter(Boolean)
        .map((part) => this.toLatin(part).charAt(0))
        .join("");
    },
    isMealOpen(key) {
      return Boolean(this.filters.search) || !this.closedMeals[key];
    },
    toggleMeal(key) {
      this.closedMeals = { ...this.closedMeals, [key]: !this.closedMeals[key] };
    },
  },
};
</script>

<style lang="scss">
.rm-orders-company {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 4px 0;
  }
}

.rm-theme .rm-orders-meal {
  &__head {
    width: 100%;
    min-height: 56px;
    padding: 10px 14px 10px 16px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    color: var(--rm-text);
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 700;
  }

  &__chevron {
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }
}

.rm-theme .rm-list.rm-orders-people {
  padding: 0 16px 8px;
  border-top: 1px solid var(--rm-divider);
}

.rm-orders-person {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 15px;
  }

  &__qty {
    font-weight: 700;
  }
}

.rm-orders-worker {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
  }
}

.rm-theme .rm-avatar--lg {
  width: 36px;
  height: 36px;
  font-size: 13px;
}
</style>
